/* Product page (pdp-main) — variant picker, quantity tiers and sticky buy bar.
   Keeps the hidden variant id and quantity, prices, badges and buy buttons in
   sync. Cart submission itself is handled by product-form.js. */

if (!customElements.get('pdp-product')) {
  customElements.define(
    'pdp-product',
    class PdpProduct extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('[data-pdp-variants]');
        if (!data) return;

        this.variants = JSON.parse(data.textContent);
        this.fieldsets = Array.from(this.querySelectorAll('[data-pdp-option]'));
        this.variantInput = this.querySelector('[data-variant-input]');
        this.quantityInput = this.querySelector('[data-quantity-input]');
        this.submits = Array.from(this.querySelectorAll('[data-submit], [data-sticky-submit]'));

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-option-input]')) this.onOptionChange();
          if (event.target.matches('[data-tier-input]')) this.onTierChange(event.target);
        });

        this.initSticky();
      }

      all(selector) {
        return this.querySelectorAll(selector);
      }

      selectedOptions() {
        return this.fieldsets.map((fieldset) => {
          const checked = fieldset.querySelector('[data-option-input]:checked');
          return checked ? checked.value : null;
        });
      }

      matches(variant, options) {
        return options.every((value, index) => variant.options[index] === value);
      }

      onOptionChange() {
        const options = this.selectedOptions();
        const variant = this.variants.find((v) => this.matches(v, options));

        this.fieldsets.forEach((fieldset, index) => {
          const current = fieldset.querySelector('[data-option-selected]');
          if (current) current.textContent = options[index];
        });

        this.render(variant);
        this.markAvailability(options);
      }

      onTierChange(input) {
        if (this.quantityInput) this.quantityInput.value = input.value;
      }

      setSubmit(disabled, label) {
        this.submits.forEach((button) => {
          button.disabled = disabled;
          const text = button.querySelector('[data-submit-label]');
          if (text) text.textContent = label;
        });
      }

      render(variant) {
        if (!variant) {
          this.setSubmit(true, this.dataset.labelUnavailable);
          return;
        }

        if (this.variantInput) {
          this.variantInput.value = variant.id;
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.all('[data-price]').forEach((el) => (el.textContent = variant.price));

        const onSale = Boolean(variant.compare);
        this.all('[data-compare]').forEach((el) => (el.hidden = !onSale));
        this.all('[data-compare-value]').forEach((el) => (el.textContent = variant.compare || ''));
        this.all('[data-save]').forEach((el) => {
          el.hidden = !onSale;
          el.textContent = (el.dataset.saveTemplate || '').replace('[percent]', variant.save);
        });
        this.all('[data-unit-price]').forEach((el) => {
          el.hidden = !variant.unit;
          el.textContent = variant.unit || '';
        });

        this.all('[data-tier-input]').forEach((input) => {
          const tier = variant.tiers[Number(input.dataset.tierIndex)];
          const label = input.nextElementSibling;
          if (!tier || !label) return;
          const price = label.querySelector('[data-tier-price]');
          const compare = label.querySelector('[data-tier-compare]');
          if (price) price.textContent = tier.price;
          if (compare) compare.textContent = tier.original;
        });

        this.setSubmit(!variant.available, variant.available ? this.dataset.labelAdd : this.dataset.labelSoldOut);

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      /* A value is marked unavailable when no in-stock variant combines it
         with the other currently selected values. */
      markAvailability(options) {
        this.fieldsets.forEach((fieldset, index) => {
          fieldset.querySelectorAll('[data-option-input]').forEach((input) => {
            const candidate = options.slice();
            candidate[index] = input.value;
            const available = this.variants.some((v) => v.available && this.matches(v, candidate));
            input.classList.toggle('is-unavailable', !available);
            const note = input.nextElementSibling && input.nextElementSibling.querySelector('[data-unavailable-note]');
            if (note) note.hidden = available;
          });
        });
      }

      /* The sticky bar is shown once the main buy button has scrolled out of
         view above the viewport. It is a visual shortcut only: its button is
         out of the tab order because the main button stays reachable. */
      initSticky() {
        const sticky = this.querySelector('[data-sticky]');
        const main = this.querySelector('[data-submit]');
        if (!sticky || !main) return;

        /* A rAF-throttled scroll check rather than an IntersectionObserver:
           the observer only fires on crossings, so a jump past the button
           (anchor link, fast fling) would leave the bar hidden. */
        let queued = false;
        const update = () => {
          queued = false;
          const passed = main.getBoundingClientRect().bottom < 0;
          sticky.classList.toggle('is-visible', passed);
          sticky.setAttribute('aria-hidden', String(!passed));
        };
        const queue = () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(update);
        };

        sticky.hidden = false;
        window.addEventListener('scroll', queue, { passive: true });
        window.addEventListener('resize', queue, { passive: true });
        update();
      }
    }
  );
}
